import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { forwardRef } from 'react';

import { calculateIconSize } from './calculateIconSize';
import { getVariant } from './getVariantStyles';

type ButtonVariants = 'primary' | 'secondary' | 'text' | 'destroy';

type ButtonOptions = {
  loading?: boolean;
  variant?: ButtonVariants;
};

export type ButtonProps<C extends React.ElementType> = Omit<
  MuiButtonProps<
    C,
    {
      component?: C;
    }
  >,
  'color' | 'variant'
> &
  ButtonOptions;

const Component = <C extends React.ElementType = 'button'>(
  props: ButtonProps<C>,
  ref: React.ForwardedRef<HTMLButtonElement>
) => {
  const { loading, variant, size, endIcon, disabled, startIcon, ...rest } = props;
  const { variant: variantStyle, color } = getVariant(variant);

  return (
    <MuiButton
      {...rest}
      ref={ref}
      size={size}
      variant={variantStyle}
      color={color}
      disabled={loading || disabled}
      startIcon={variant === 'destroy' ? <DeleteOutlined data-testid="DeleteOutlineIcon" /> : startIcon}
      endIcon={loading ? <CircularProgress size={calculateIconSize(size)} /> : endIcon}
    />
  );
};

export const Button = forwardRef(Component) as <C extends React.ElementType = 'button'>(
  props: ButtonProps<C>
) => React.ReactElement;
